import React from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, Mail, FileText, FileSignature, Video, Plus, Search, Trash2, Edit3, Paperclip, ExternalLink, X, Copy, Check, Upload, Download, Receipt, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "all", label: "All", icon: Sparkles, colour: "text-blue-600" },
  { value: "email", label: "Email", icon: Mail, colour: "text-blue-600" },
  { value: "proposal", label: "Proposals", icon: FileText, colour: "text-emerald-600" },
  { value: "contract", label: "Contracts", icon: FileSignature, colour: "text-violet-600" },
  { value: "video", label: "Videos", icon: Video, colour: "text-rose-600" },
  { value: "other", label: "Other", icon: Paperclip, colour: "text-slate-600" },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

export default function SalesTools() {
  const [items, setItems] = React.useState([]);
  const [category, setCategory] = React.useState("all");
  const [search, setSearch] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [editing, setEditing] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category !== "all") params.set("category", category);
    if (search.trim()) params.set("q", search.trim());
    const r = await api.get(`/sales-tools?${params.toString()}`);
    setItems(r.data);
    setLoading(false);
  }, [category, search]);

  React.useEffect(() => { load(); }, [load]);

  const counts = React.useMemo(() => {
    const c = { all: 0, email: 0, proposal: 0, contract: 0, video: 0, other: 0 };
    for (const it of items) {
      c.all++;
      c[it.category] = (c[it.category] || 0) + 1;
    }
    return c;
  }, [items]);

  const openAdd = () => { setEditing(null); setShowForm(true); };
  const openEdit = (item) => { setEditing(item); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const onSaved = () => { closeForm(); load(); };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this sales tool?")) return;
    await api.delete(`/sales-tools/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6" data-testid="sales-tools-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Resources</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Sales Tools</h1>
          <p className="text-slate-500 text-sm mt-1">Email templates, proposals, contracts and videos for the sales team to reuse.</p>
        </div>
        <button onClick={openAdd}
          data-testid="sales-tools-add-btn"
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center gap-2">
          <Plus className="h-4 w-4" /> Add tool
        </button>
      </div>

      {/* Quotation Tool — promoted tile */}
      <Link
        to="/app/sales-tools/quotes"
        data-testid="open-quote-builder"
        className="group block bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg p-6 shadow-sm hover:shadow-md transition"
      >
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0">
              <Receipt className="h-6 w-6" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-blue-100 font-mono">Document generator</div>
              <h2 className="font-heading text-xl font-bold mt-1">Quotation Tool</h2>
              <p className="text-sm text-blue-50 mt-1 max-w-xl">
                Build branded Customer Work Order PDFs in seconds. Pick customer, term, products and payment frequency —
                download a ready-to-sign document named <code className="bg-white/10 px-1 rounded text-xs">Customer_Product_Term_Month.pdf</code>.
              </p>
            </div>
          </div>
          <ArrowRight className="h-6 w-6 flex-shrink-0 group-hover:translate-x-1 transition" />
        </div>
      </Link>

      <div className="flex items-center gap-3 flex-wrap" data-testid="sales-tools-categories">
        {CATEGORIES.map(({ value, label, icon: Icon, colour }) => (
          <button
            key={value}
            data-testid={`sales-tools-category-${value}`}
            onClick={() => setCategory(value)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors flex items-center gap-2 ${
              category === value
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-slate-700 border-slate-200 hover:border-blue-300"
            }`}
          >
            <Icon className={`h-3.5 w-3.5 ${category === value ? "text-white" : colour}`} />
            {label}
            <span className={`text-[10px] font-mono ${category === value ? "opacity-90" : "opacity-50"}`}>{counts[value] || 0}</span>
          </button>
        ))}
        <div className="flex-1 min-w-[240px] relative ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, description or tag…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="sales-tools-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 font-mono text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <Card className="border-slate-200 p-12 text-center">
          <Sparkles className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-heading text-lg text-slate-700">No sales tools yet</div>
          <div className="text-sm text-slate-500 mt-1">
            Add your first email template, proposal, contract or video.
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((it) => (
            <SalesToolCard key={it.id} item={it} onEdit={() => openEdit(it)} onDelete={() => onDelete(it.id)} />
          ))}
        </div>
      )}

      {showForm && (
        <SalesToolForm item={editing} onClose={closeForm} onSaved={onSaved} />
      )}
    </div>
  );
}

function SalesToolCard({ item, onEdit, onDelete }) {
  const meta = CATEGORIES.find((c) => c.value === item.category) || CATEGORIES[5];
  const Icon = meta.icon;
  const [copied, setCopied] = React.useState(false);

  const copyBody = async () => {
    if (!item.body) return;
    try {
      await navigator.clipboard.writeText(item.body);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const videoEmbed = parseVideoEmbed(item.video_url);

  return (
    <Card className="border-slate-200 hover:border-blue-300 transition-colors" data-testid={`sales-tool-card-${item.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Icon className={`h-4 w-4 ${meta.colour}`} />
              <span className="text-[10px] uppercase font-mono tracking-wider text-slate-500">{meta.label}</span>
            </div>
            <div className="font-heading text-lg font-bold text-slate-900 mt-1 truncate">{item.title}</div>
            {item.description && (
              <div className="text-sm text-slate-600 mt-1 line-clamp-2">{item.description}</div>
            )}
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <button onClick={onEdit} className="p-1.5 text-slate-400 hover:text-blue-600" title="Edit" data-testid={`sales-tool-edit-${item.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
            <button onClick={onDelete} className="p-1.5 text-slate-400 hover:text-red-600" title="Delete" data-testid={`sales-tool-delete-${item.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        </div>

        {videoEmbed && (
          <div className="mt-3 rounded-md overflow-hidden border border-slate-200" style={{ aspectRatio: "16/9" }}>
            <iframe src={videoEmbed} title={item.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
        )}
        {!videoEmbed && item.video_url && (
          <a href={item.video_url} target="_blank" rel="noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-sm text-rose-600 hover:underline">
            <Video className="h-3.5 w-3.5" /> Watch video <ExternalLink className="h-3 w-3" />
          </a>
        )}

        {item.body && (
          <details className="mt-3">
            <summary className="cursor-pointer text-xs font-medium text-blue-700 hover:underline">View content</summary>
            <pre className="mt-2 p-3 text-[12px] text-slate-700 bg-slate-50 rounded border border-slate-200 whitespace-pre-wrap font-sans max-h-64 overflow-y-auto">{item.body}</pre>
            <button onClick={copyBody}
              className="mt-2 px-2 py-1 text-xs border border-slate-300 rounded hover:border-blue-400 flex items-center gap-1"
              data-testid={`sales-tool-copy-${item.id}`}>
              {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
              {copied ? "Copied" : "Copy text"}
            </button>
          </details>
        )}

        {item.external_url && (
          <a href={item.external_url} target="_blank" rel="noreferrer"
            className="mt-3 block text-xs text-blue-600 hover:underline truncate" data-testid={`sales-tool-link-${item.id}`}>
            <ExternalLink className="inline h-3 w-3 mr-1" /> {item.external_url}
          </a>
        )}

        {(item.attachments || []).length > 0 && (
          <div className="mt-3 space-y-1">
            {item.attachments.map((a) => (
              <a key={a.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/sales-tools/attachments/${a.id}`}
                target="_blank" rel="noreferrer"
                className="flex items-center gap-2 text-xs text-slate-600 hover:text-blue-600">
                <Paperclip className="h-3 w-3" /> {a.filename} <Download className="h-3 w-3 opacity-50" />
              </a>
            ))}
          </div>
        )}

        {item.tags?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {item.tags.map((t) => (
              <span key={t} className="px-2 py-0.5 text-[10px] uppercase font-mono tracking-wider bg-slate-100 text-slate-600 rounded">
                {t}
              </span>
            ))}
          </div>
        )}

        <div className="mt-3 text-[10px] text-slate-400 font-mono">
          Added {fmtDate(item.created_at)} by {item.created_by}
        </div>
      </CardContent>
    </Card>
  );
}

function parseVideoEmbed(url) {
  if (!url) return null;
  // YouTube
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  // Loom
  const loom = url.match(/loom\.com\/share\/([A-Za-z0-9]+)/);
  if (loom) return `https://www.loom.com/embed/${loom[1]}`;
  // Vimeo
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

function SalesToolForm({ item, onClose, onSaved }) {
  const isEdit = !!item;
  const [form, setForm] = React.useState({
    title: item?.title || "",
    category: item?.category || "email",
    description: item?.description || "",
    body: item?.body || "",
    video_url: item?.video_url || "",
    external_url: item?.external_url || "",
    tags: (item?.tags || []).join(", "),
  });
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  // Pending files staged for a brand-new tool. They're held locally and
  // uploaded in a follow-up pass right after the POST returns the new id,
  // so an SDR can attach a deck and a PDF in one go when creating a tool.
  const [pendingFiles, setPendingFiles] = React.useState([]);
  const fileRef = React.useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadOne = async (toolId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/sales-tools/${toolId}/attachments`, fd,
                   { headers: { "Content-Type": "multipart/form-data" } });
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(),
        category: form.category,
        description: form.description.trim() || null,
        body: form.body || null,
        video_url: form.video_url.trim() || null,
        external_url: form.external_url.trim() || null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (isEdit) {
        await api.patch(`/sales-tools/${item.id}`, body);
        toast.success("Saved");
      } else {
        const r = await api.post("/sales-tools", body);
        const newId = r.data?.id;
        if (newId && pendingFiles.length > 0) {
          // Upload sequentially so a bad file surfaces a clear error without
          // partially corrupting the batch.
          for (const f of pendingFiles) {
            try {
              await uploadOne(newId, f);
            } catch (err) {
              toast.error(`Attachment "${f.name}" failed — ${err?.response?.data?.detail || "upload error"}`);
            }
          }
        }
        toast.success(pendingFiles.length > 0
          ? `Added — ${pendingFiles.length} attachment${pendingFiles.length === 1 ? "" : "s"} uploaded`
          : "Added");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (file) => {
    if (!isEdit) {
      // Queue the file for upload-after-create.
      setPendingFiles((prev) => [...prev, file]);
      return;
    }
    setUploading(true);
    try {
      await uploadOne(item.id, file);
      toast.success("Uploaded");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="sales-tool-form">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div className="font-heading text-lg font-bold">{isEdit ? "Edit sales tool" : "Add sales tool"}</div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Title *</label>
            <input value={form.title} onChange={(e) => update("title", e.target.value)}
              data-testid="sales-tool-title"
              className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Category *</label>
            <div className="mt-1 grid grid-cols-2 sm:grid-cols-5 gap-2">
              {CATEGORIES.filter((c) => c.value !== "all").map(({ value, label, icon: Icon }) => (
                <button key={value} type="button"
                  data-testid={`sales-tool-form-category-${value}`}
                  onClick={() => update("category", value)}
                  className={`flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-medium rounded-md border ${form.category === value ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Short description</label>
            <input value={form.description} onChange={(e) => update("description", e.target.value)}
              data-testid="sales-tool-description"
              className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Content (e.g. email body, contract clauses)</label>
            <textarea value={form.body} onChange={(e) => update("body", e.target.value)} rows={8}
              data-testid="sales-tool-body"
              placeholder="Paste your email template or contract text here. Supports {{placeholders}} for personalisation."
              spellCheck="true"
              className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Video URL (YouTube / Loom / Vimeo)</label>
              <input value={form.video_url} onChange={(e) => update("video_url", e.target.value)}
                data-testid="sales-tool-video"
                placeholder="https://www.loom.com/share/…"
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-slate-500">External link</label>
              <input value={form.external_url} onChange={(e) => update("external_url", e.target.value)}
                data-testid="sales-tool-external"
                placeholder="https://drive.google.com/…"
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Tags (comma-separated)</label>
            <input value={form.tags} onChange={(e) => update("tags", e.target.value)}
              data-testid="sales-tool-tags"
              placeholder="cyber, prospecting, msp"
              className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Attachments</label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              {isEdit && (item.attachments || []).map((a) => (
                <a key={a.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/sales-tools/attachments/${a.id}`} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-50 border border-slate-200 rounded hover:border-blue-300">
                  <Paperclip className="h-3 w-3" /> {a.filename}
                </a>
              ))}
              {!isEdit && pendingFiles.map((f, idx) => (
                <span key={`${f.name}-${idx}`}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded"
                      data-testid={`sales-tool-pending-${idx}`}
                      title="Queued — will upload after Add tool">
                  <Paperclip className="h-3 w-3" /> {f.name}
                  <button type="button"
                          onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
                          className="ml-1 text-blue-600 hover:text-blue-900"
                          aria-label={`Remove ${f.name}`}
                          data-testid={`sales-tool-pending-remove-${idx}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input ref={fileRef} type="file" multiple className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  files.forEach((f) => onUpload(f));
                  e.target.value = "";
                }}
                data-testid="sales-tool-file-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                data-testid="sales-tool-attach-btn"
                className="px-2 py-1 text-xs border border-slate-300 rounded hover:border-blue-400 flex items-center gap-1 disabled:opacity-50">
                <Upload className="h-3 w-3" /> {uploading ? "Uploading…" : "Attach file"}
              </button>
            </div>
            {!isEdit && (
              <p className="mt-1.5 text-[11px] text-slate-500 italic">
                {pendingFiles.length === 0
                  ? "Attach decks, PDFs or images — they'll upload the moment the tool is created."
                  : `${pendingFiles.length} file${pendingFiles.length === 1 ? "" : "s"} queued · will upload on save`}
              </p>
            )}
          </div>
        </div>
        <div className="p-5 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-md hover:border-slate-400">Cancel</button>
          <button onClick={save} disabled={saving}
            data-testid="sales-tool-save"
            className="px-5 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add tool"}
          </button>
        </div>
      </div>
    </div>
  );
}
